import {useIsMaterialUnmeasured} from '@domain/material-storage'
import {mapToMaterialsById, MaterialSource, useMaterials, type Material} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {usePlantStartOfToday, useUpmPlantContext} from '@hconnect/upm'
import {Stack} from '@mui/material'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Navigate, useLocation, useNavigate} from 'react-router'

import {PlannerBackButton, ManageOrdersSection} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useUrlParam, MomentRange} from '../hooks'
import {PlannerSkeleton, StockPageLayout} from '../layouts'
import {
  SelectedTimeframeProvider,
  SelectedMaterialOrderProvider,
  useStorageLevelsRoute
} from '../providers'

import {CreateEditMaterialOrderDialog} from './createEditMaterialOrder'
import {DetailedStockDevelopmentCard} from './DetailedStockDevelopmentCard'
import {MaterialOrdersTitleActions} from './MaterialOrdersTitleActions'

export const MaterialOrdersContainer = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()
  const location = useLocation()
  const startOfToday = usePlantStartOfToday()
  const {getMaterialsPath, getSilosPath} = useStorageLevelsRoute()

  // TODO: refactor this using custom hook like useUrlParam
  const {state} = location
  const routingState = state as {materialsVisitedFrom?: 'storage' | 'material'} | undefined

  const {plantId: plantCode} = useUpmPlantContext()
  const materialId = useUrlParam('materialId')
  const {data: materialsById} = useMaterials({
    plantCode,
    options: {
      select: mapToMaterialsById
    }
  })

  if (!materialsById) {
    throw new Error('BUG: materialsById should be loaded before rendering this component')
  }

  const currentMaterial: Material = materialsById[materialId]

  const {data: isMaterialUnmeasured, isLoading: materialIsUnmeasuredIsLoading} =
    useIsMaterialUnmeasured(currentMaterial.id, plantCode)

  const defaultTimeframe: MomentRange = useMemo(() => {
    const start = startOfToday.clone().subtract(1, 'day').startOf('day')
    const end = startOfToday.clone().add(9, 'days')
    return [start, end]
  }, [startOfToday])

  if (materialIsUnmeasuredIsLoading) {
    return <PlannerSkeleton height="65vh" />
  }

  const isBoughtFromVendor = currentMaterial.source === MaterialSource.BoughtFromVendor

  if (!(isMaterialUnmeasured || isBoughtFromVendor)) {
    return <Navigate to="/404" replace />
  }

  const canShowOrders = currentMaterial.products.length > 0

  if (!isMaterialUnmeasured && !canShowOrders) {
    const redirectPath = `${getMaterialsPath()}?materialType=${currentMaterial.type}`
    return <Navigate to={redirectPath} replace />
  }

  const handleGoBack = () => {
    if (routingState?.materialsVisitedFrom === 'storage') {
      const pathname = `${getSilosPath()}?materialType=${currentMaterial.type}`
      void navigate(pathname)
    } else {
      const pathname = `${getMaterialsPath()}?materialType=${currentMaterial.type}`
      void navigate(pathname)
    }
  }

  return (
    <SelectedTimeframeProvider defaultTimeframe={defaultTimeframe}>
      <SelectedMaterialOrderProvider>
        <StockPageLayout
          py={3}
          aboveTitle={
            <PlannerBackButton
              text={
                routingState?.materialsVisitedFrom === 'storage'
                  ? t('navItems.backToSilos')
                  : t('navItems.backToMaterials')
              }
              onGoBack={handleGoBack}
              {...dataTestId(
                routingState?.materialsVisitedFrom === 'storage'
                  ? 'back_to_silos_button'
                  : 'back_to_materials_button'
              )}
            />
          }
          headerActionContent={<MaterialOrdersTitleActions canAddMaterialOrder={canShowOrders} />}
          title={currentMaterial.name}
          {...dataTestId('page_materialOrders')}
        >
          <Stack spacing={2}>
            <DetailedStockDevelopmentCard />
            {canShowOrders && <ManageOrdersSection />}
          </Stack>
          {canShowOrders && <CreateEditMaterialOrderDialog />}
        </StockPageLayout>
      </SelectedMaterialOrderProvider>
    </SelectedTimeframeProvider>
  )
}
